import { useEffect } from "react";

/**
 * iOS Safari auto-zooms when focusing inputs with font-size < 16px.
 * This hook resets the viewport scale back to 1 on blur as a safety net.
 * It also ensures the viewport meta tag has maximum-scale=1 while an input is focused.
 */
export function useIOSAutoZoomFix() {
  useEffect(() => {
    // Only apply on iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) return;

    const resetZoom = () => {
      // Small delay to let iOS finish its blur animation
      setTimeout(() => {
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          // Temporarily force scale reset
          const original = viewport.getAttribute("content") || "";
          viewport.setAttribute(
            "content",
            "width=device-width, initial-scale=1, maximum-scale=1"
          );
          // Restore original after reset takes effect
          requestAnimationFrame(() => {
            viewport.setAttribute("content", original);
          });
        }
      }, 50);
    };

    const handleBlur = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        resetZoom();
      }
    };

    document.addEventListener("blur", handleBlur, true);
    return () => document.removeEventListener("blur", handleBlur, true);
  }, []);
}
